import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, Play, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { glowPointerHandlers } from '@/lib/glowPointer';
import Robot from '@/components/Robot';
import ThemeWrapper from '@/components/ThemeWrapper';
import { useRobotVoice, ROBOT_SPEECH_RATE } from '@/hooks/useRobotVoice';
import { useApp } from '@/context/AppContext';
import { robotTextOrDefault } from '@/lib/robotText';
import { isSpeechSynthesisAvailable, primeSpeechSynthesisFromUserInteraction } from '@/lib/nativeTts';
import { isMobileOrTabletClient } from '@/lib/deviceGate';
import { cn } from '@/lib/utils';

/** loading : attente API ; ready : réglages NocodeBackend chargés ; error : pas de connexion / échec */
const loadSettingsOnce = async (fetchSettings) => {
  const data = await fetchSettings();
  return data ? 'ready' : 'error';
};

const WelcomePage = () => {
  const navigate = useNavigate();
  const { speak, isSpeaking, speechSpectrum } = useRobotVoice();
  const { fetchSettings, settings } = useApp();
  const [settingsPhase, setSettingsPhase] = useState('loading');
  const hasSpokenRef = useRef(false);
  const isStartingRef = useRef(false);
  const welcomeTextRef = useRef(robotTextOrDefault(null, 'welcome'));
  const isMobileGuest = useMemo(() => isMobileOrTabletClient(), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSettingsPhase('loading');
      const phase = await loadSettingsOnce(fetchSettings);
      if (cancelled) return;
      setSettingsPhase(phase);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchSettings]);

  useEffect(() => {
    welcomeTextRef.current = robotTextOrDefault(settings, 'welcome');
  }, [settings]);

  useEffect(() => {
    if (isMobileGuest) return undefined;
    if (settingsPhase !== 'ready') return undefined;
    if (!isSpeechSynthesisAvailable()) return undefined;

    try {
      window.speechSynthesis.getVoices();
    } catch {
      /* ignore */
    }

    const entranceEndAt = Date.now() + 750;
    const waitEntrance = () =>
      new Promise((resolve) => {
        const ms = Math.max(0, entranceEndAt - Date.now());
        if (ms <= 0) resolve();
        else setTimeout(resolve, ms);
      });

    let cancelled = false;
    let gestureVerifyTimer = null;

    const speakNativeOnce = () => {
      if (!window?.speechSynthesis) return Promise.resolve(false);
      if (hasSpokenRef.current) return Promise.resolve(true);
      return new Promise((resolve) => {
        let done = false;
        const finish = (ok) => {
          if (done) return;
          done = true;
          resolve(ok);
        };
        const synth = window.speechSynthesis;
        const u = new SpeechSynthesisUtterance(welcomeTextRef.current);
        u.lang = 'fr-FR';
        u.rate = ROBOT_SPEECH_RATE;
        u.pitch = 2.0;
        u.volume = 1;
        u.onstart = () => finish(true);
        u.onend = () => finish(true);
        u.onerror = () => finish(false);
        synth.speak(u);
        setTimeout(() => finish(false), 15000);
      });
    };

    const playWelcomeReliable = async () => {
      if (hasSpokenRef.current) return true;
      const startedByHook = await speak(welcomeTextRef.current);
      if (startedByHook) return true;
      const startedNative = await speakNativeOnce();
      return startedNative;
    };

    const markStarted = () => {
      if (cancelled || hasSpokenRef.current) return;
      hasSpokenRef.current = true;
    };

    const tryAutoSpeak = async () => {
      await waitEntrance();
      if (cancelled || hasSpokenRef.current) return;
      const synth = window.speechSynthesis;
      if (!synth || hasSpokenRef.current || isStartingRef.current) return;
      isStartingRef.current = true;
      const ok = await playWelcomeReliable();
      isStartingRef.current = false;
      if (ok) markStarted();
    };

    tryAutoSpeak();
    const onVoicesChanged = () => {
      if (hasSpokenRef.current) return;
      tryAutoSpeak();
    };
    window.speechSynthesis.addEventListener('voiceschanged', onVoicesChanged);

    const trySpeakAfterGesture = (event) => {
      const target = event?.target;
      if (target instanceof Element) {
        if (target.closest('[data-testid="admin-gear-btn"]')) return;
        if (target.closest('[data-testid="start-btn"]')) return;
        if (target.closest('[data-testid="settings-retry-btn"]')) return;
      }

      primeSpeechSynthesisFromUserInteraction();

      /* Après le premier message entendu : tout clic ailleurs sur la page relance l’accueil (synthèse vocale). */
      if (hasSpokenRef.current) {
        void speak(welcomeTextRef.current);
        return;
      }

      if (gestureVerifyTimer) {
        clearTimeout(gestureVerifyTimer);
        gestureVerifyTimer = null;
      }

      if (isStartingRef.current) return;

      const clearGestureSafety = () => {
        if (gestureVerifyTimer) {
          clearTimeout(gestureVerifyTimer);
          gestureVerifyTimer = null;
        }
      };

      const fallbackPlay = () => {
        if (cancelled || hasSpokenRef.current || isStartingRef.current) return;
        isStartingRef.current = true;
        void playWelcomeReliable().then((ok) => {
          isStartingRef.current = false;
          if (cancelled || !ok) return;
          markStarted();
        });
      };

      gestureVerifyTimer = window.setTimeout(() => {
        gestureVerifyTimer = null;
        if (cancelled || hasSpokenRef.current || isStartingRef.current) return;
        fallbackPlay();
      }, 4500);

      isStartingRef.current = true;
      void speak(welcomeTextRef.current).then((ok) => {
        isStartingRef.current = false;
        clearGestureSafety();
        if (cancelled || hasSpokenRef.current) return;
        if (ok) {
          markStarted();
        } else {
          fallbackPlay();
        }
      });
    };
    window.addEventListener('pointerdown', trySpeakAfterGesture, { passive: true });
    window.addEventListener('keydown', trySpeakAfterGesture);

    return () => {
      cancelled = true;
      if (gestureVerifyTimer) clearTimeout(gestureVerifyTimer);
      window.speechSynthesis.removeEventListener('voiceschanged', onVoicesChanged);
      window.removeEventListener('pointerdown', trySpeakAfterGesture);
      window.removeEventListener('keydown', trySpeakAfterGesture);
    };
  }, [speak, settingsPhase, isMobileGuest]);

  const handleRetrySettings = async () => {
    window.speechSynthesis?.cancel();
    hasSpokenRef.current = false;
    setSettingsPhase('loading');
    const data = await fetchSettings();
    setSettingsPhase(data ? 'ready' : 'error');
    if (data) welcomeTextRef.current = robotTextOrDefault(data, 'welcome');
  };

  const handleStart = () => {
    primeSpeechSynthesisFromUserInteraction();
    window.speechSynthesis?.cancel?.();
    navigate('/questions');
  };

  const titleText = settings?.ui_texts?.welcome_title || 'Salut !';
  const subtitleText =
    settings?.ui_texts?.welcome_subtitle ||
    'Je suis ton ami robot. Ici, tu peux me parler de tout.';
  const startLabel = settings?.ui_texts?.start_button || "C'est parti !";

  const showContent = settingsPhase === 'ready' && settings;
  const showError = settingsPhase === 'error';

  return (
    <ThemeWrapper className="flex flex-col items-center justify-center p-6 md:p-12">
      <button
        data-testid="admin-gear-btn"
        onClick={() => navigate('/admin')}
        type="button"
        className={cn(
          'absolute top-4 right-4 p-3 text-white/30 hover:text-cyan-400 transition-all gear-icon z-20',
          isMobileGuest && 'z-[250]'
        )}
        aria-label="Panneau administrateur"
      >
        <Settings className="w-5 h-5" />
      </button>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.45 }}
        className="flex w-full max-w-2xl flex-col items-center gap-8 md:gap-12 z-10 text-center"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <Robot isSpeaking={isSpeaking} speechSpectrum={speechSpectrum} size="large" />
        </motion.div>

        {/*
          Hauteur réservée ≈ titre + sous-titre + bouton : évite le recentrage vertical
          (robot « qui saute ») quand les textes arrivent depuis l’API.
        */}
        <div className="flex w-full min-h-[clamp(17.5rem,42vh,30rem)] flex-col items-center justify-start md:min-h-[clamp(19rem,44vh,32rem)]">
          <AnimatePresence mode="wait">
            {showError ? (
              <motion.div
                key="welcome-error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.38, ease: 'easeOut' }}
                className="space-y-4 px-2"
              >
                <h1 className="font-bold text-3xl md:text-5xl text-glow text-amber-100">
                  Connexion au site impossible
                </h1>
                <p className="text-lg text-white/85 max-w-md mx-auto leading-relaxed">
                  Les messages du professeur sont sur le serveur (NoCodeBackend). Sans cette connexion, tu vois
                  une page vide de ces textes — ce n’est pas un bug de ton ordinateur, c’est le réseau ou
                  l’URL du site.
                </p>
                <Button
                  data-testid="settings-retry-btn"
                  type="button"
                  onClick={() => void handleRetrySettings()}
                  className="mt-2 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold text-lg py-6 px-8 rounded-full inline-flex items-center gap-2"
                >
                  <RefreshCw className="w-5 h-5" />
                  Réessayer
                </Button>
              </motion.div>
            ) : showContent ? (
              <motion.div
                key="welcome-ready"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="flex w-full flex-col items-center gap-8 md:gap-10"
              >
                <div className="space-y-4 text-center">
                  <h1
                    data-testid="welcome-title"
                    className="font-bold text-5xl md:text-7xl tracking-tight text-glow min-h-[1.15em]"
                  >
                    {titleText}
                  </h1>
                  <p
                    data-testid="welcome-subtitle"
                    className="text-xl md:text-2xl font-medium leading-relaxed text-white/80 max-w-md mx-auto min-h-[1.25em]"
                  >
                    {subtitleText}
                  </p>
                </div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.4, ease: 'easeOut' }}
                >
                  <Button
                    data-testid="start-btn"
                    {...glowPointerHandlers}
                    onClick={handleStart}
                    className="btn-glow btn-radial-cyan-blue text-white font-bold text-xl md:text-2xl py-6 px-12 md:py-8 md:px-16 rounded-full flex items-center gap-3"
                  >
                    <Play className="w-6 h-6 fill-current" />
                    {startLabel}
                  </Button>
                </motion.div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </motion.div>
    </ThemeWrapper>
  );
};

export default WelcomePage;
